import { z } from "zod";
import isEqual from "react-fast-compare";
import { DefaultPlanResponse } from "@/shared/utils/QuoteAPI";
import { PublicConfig } from "@/shared/PublicConfig";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { CoverageSettings, CoverageType, Quote } from "@/shared/types/Quote.interface";

export interface DefaultAmounts {
    annualLimit?: number;
    annualDeductible?: number;
    reimbursementRate?: number;
}

const ValidLevelsSchema = z.enum(["Level1", "Level2"]);
export const ValidDeductiblesSchema = z.union([z.literal(100), z.literal(250), z.literal(500), z.literal(750), z.literal(1000)]);
export const ValidReimbursementRatesSchema = z.union([z.literal(70), z.literal(80), z.literal(90)]);
export const ValidAnnualLimitsSchema = z.union([
    z.literal(PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE),
    z.literal(2500),
    z.literal(3000),
    z.literal(4000),
    z.literal(5000),
    z.literal(7000),
    z.literal(10000)
]);

// Plan ID format validation with unlimited support
const PlanIdFormatSchema = z.string().transform(val => val.toLowerCase());

const ParsedPlanConfigSchema = z.object({
    level: z.number().int().min(1).max(2).optional(),
    annualDeductible: ValidDeductiblesSchema.optional(),
    annualLimit: ValidAnnualLimitsSchema.optional(),
    reimbursementRate: ValidReimbursementRatesSchema.optional()
});

export type ParsedPlanConfig = z.infer<typeof ParsedPlanConfigSchema>;

export class DefaultPlanUtils {
    /**
     * Creates a fallback DefaultPlanResponse that matches actual API response structure
     */
    static createFallbackDefaultPlan(underwriter: PetUnderwriterType): DefaultPlanResponse {
        const underWriterToLowerCase = underwriter.toLowerCase();
        switch (underWriterToLowerCase) {
            case "ptz-us":
                return {
                    id: PublicConfig.PTZ_US.DEFAULT_PLAN_ID,
                    type: ["accident", "illness"],
                    annualLimit: {
                        symbol: "$",
                        value: PublicConfig.PTZ_US.DEFAULT_ANNUAL_LIMIT
                    },
                    reimbursementPercent: {
                        symbol: "%",
                        value: PublicConfig.PTZ_US.DEFAULT_REIMBURSMENT_RATE
                    },
                    deductible: {
                        symbol: "$",
                        value: PublicConfig.PTZ_US.DEFAULT_ANNUAL_DEDUCTIBLE
                    }
                };
            case "ptz-ca":
                return {
                    id: PublicConfig.PTZ_CA.DEFAULT_PLAN_ID,
                    type: ["accident", "illness", "peril"], // CA includes peril
                    annualLimit: {
                        symbol: "$",
                        value: PublicConfig.PTZ_CA.DEFAULT_ANNUAL_LIMIT
                    }
                    // No reimbursementPercent or deductible - matches CA API response
                };
            default:
                throw new Error(`Unsupported underwriter: ${underwriter}`);
        }
    }

    /**
     * Converts DefaultPlanResponse to simple numbers, respecting underwriter capabilities
     */
    static extractDefaultAmounts(defaultPlan?: DefaultPlanResponse, underwriter: PetUnderwriterType = "ptz-us"): DefaultAmounts {
        const fallbackPlan = DefaultPlanUtils.createFallbackDefaultPlan(underwriter);

        if (!defaultPlan) {
            return {
                annualLimit: fallbackPlan.annualLimit?.value,
                annualDeductible: fallbackPlan.deductible?.value,
                reimbursementRate: fallbackPlan.reimbursementPercent?.value
            };
        }

        const result: DefaultAmounts = {
            annualLimit: defaultPlan.annualLimit?.value ?? fallbackPlan.annualLimit?.value,
            annualDeductible: defaultPlan.deductible?.value ?? fallbackPlan.deductible?.value,
            reimbursementRate: defaultPlan.reimbursementPercent?.value ?? fallbackPlan.reimbursementPercent?.value
        };

        return result;
    }

    /**
     * Converts DefaultPlanResponse directly to CoverageSettings format
     * with fallbacks to PublicConfig values
     */
    static defaultPlanToCoverageSettings(defaultPlan?: DefaultPlanResponse, underwriter: PetUnderwriterType = "ptz-us", extra?: Record<string, any>): CoverageSettings {
        const fallbackPlan = DefaultPlanUtils.createFallbackDefaultPlan(underwriter);
        const planToUse = defaultPlan ?? fallbackPlan;

        // Extract coverage types and create coverage array
        const coverages = (planToUse.type ?? ["accident", "illness"]).map(type => ({
            type: type as any, // Type assertion needed for the enum
            id: planToUse.id
        }));

        // Extract amounts with fallbacks
        const amounts = {
            reimbursementRate: planToUse.reimbursementPercent?.value ?? fallbackPlan.reimbursementPercent?.value,
            annualDeductible: planToUse.deductible?.value ?? fallbackPlan.deductible?.value,
            annualLimit: planToUse.annualLimit?.value ?? fallbackPlan.annualLimit?.value
        };

        return {
            coverages,
            amounts,
            extra: extra ?? {}
        };
    }

    /**
     * Generate the standard plan ID format from parsed values
     * Example: level=2, deductible=500, reimbursementRate=70 -> "Level2~Deductible500~Copay30"
     */
    static generateStandardPlanId(level: number, annualDeductible: number, reimbursementRate: number): string {
        const copayPercent = 100 - reimbursementRate;
        return `Level${level}~Deductible${annualDeductible}~Copay${copayPercent}`;
    }

    /**
     * Get coverage types based on level (US only)
     */
    static getCoveragesForLevel(level: number, standardPlanId: string): CoverageType[] {
        switch (level) {
            case 1:
                // Level 1 = Accident Only
                return [
                    {
                        type: "accident",
                        id: standardPlanId
                    }
                ];

            case 2:
            default:
                // Level 2 = Accident + Illness (default for any unrecognized level)
                return [
                    {
                        type: "accident",
                        id: standardPlanId
                    },
                    {
                        type: "illness",
                        id: standardPlanId
                    }
                ];
        }
    }

    /**
     * Parse plan ID format with validation and unlimited support
     * Example: "level2-limitunlimited-deductible500-reimbursement70"
     */
    static parsePlanId(planId: string): ParsedPlanConfig {
        // Validate format first
        const validatedPlanId = PlanIdFormatSchema.parse(planId);

        const result: ParsedPlanConfig = {};
        const parts = validatedPlanId.split("-");

        for (const part of parts) {
            const levelMatch = part.match(/^level(\d+)$/);
            if (levelMatch && levelMatch[1]) {
                try {
                    const levelNumber = parseInt(levelMatch[1], 10);
                    const levelString = `Level${levelNumber}`;

                    // Validate against the ValidLevelsSchema
                    ValidLevelsSchema.parse(levelString);

                    // If validation passes, store the number
                    result.level = levelNumber;
                } catch (error) {
                    console.warn(`Invalid level "${levelMatch[1]}", using fallback:`, error);
                }
                continue;
            }

            // Handle unlimited annual limit
            if (part === "limitunlimited") {
                result.annualLimit = ValidAnnualLimitsSchema.parse(PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE);
                continue;
            }

            const limitMatch = part.match(/^limit(\d+)$/);
            if (limitMatch && limitMatch[1]) {
                result.annualLimit = ValidAnnualLimitsSchema.parse(parseInt(limitMatch[1], 10));
                continue;
            }

            const deductibleMatch = part.match(/^deductible(\d+)$/);
            if (deductibleMatch && deductibleMatch[1]) {
                result.annualDeductible = ValidDeductiblesSchema.parse(parseInt(deductibleMatch[1], 10));
                continue;
            }

            const reimbursementMatch = part.match(/^reimbursement(\d+)$/);
            if (reimbursementMatch && reimbursementMatch[1]) {
                result.reimbursementRate = ValidReimbursementRatesSchema.parse(parseInt(reimbursementMatch[1], 10));
                continue;
            }
        }

        // Final validation of the complete parsed object
        return ParsedPlanConfigSchema.parse(result);
    }

    /**
     * Transform plan ID to full CoverageSettings
     */
    static transformPlanToCoverageSettings(planId: string, fallbackDefaults?: DefaultPlanResponse): CoverageSettings {
        const parsed = DefaultPlanUtils.parsePlanId(planId);
        const fallbackPlan = DefaultPlanUtils.createFallbackDefaultPlan("ptz-us");
        const defaults = fallbackDefaults ?? fallbackPlan;

        // Extract values with fallbacks
        const level = parsed.level ?? 2; // Default to Level 2
        const annualDeductible = parsed.annualDeductible ?? defaults.deductible?.value ?? PublicConfig.PTZ_US.DEFAULT_ANNUAL_DEDUCTIBLE;
        const annualLimit = parsed.annualLimit ?? defaults.annualLimit?.value ?? PublicConfig.PTZ_US.DEFAULT_ANNUAL_LIMIT;
        const reimbursementRate = parsed.reimbursementRate ?? defaults.reimbursementPercent?.value ?? PublicConfig.PTZ_US.DEFAULT_REIMBURSMENT_RATE;

        // Generate the standard plan ID format
        const standardPlanId = DefaultPlanUtils.generateStandardPlanId(level, annualDeductible, reimbursementRate);

        // Get coverages for the level
        const coverages = DefaultPlanUtils.getCoveragesForLevel(level, standardPlanId);

        return {
            coverages,
            amounts: {
                reimbursementRate,
                annualDeductible,
                annualLimit
            },
            extra: {}
        };
    }

    /**
     * Get coverage settings from query parameters
     * Handles external URLs with planId parameter
     */
    static getCoverageSettingsFromQuery(planIdQueryValue: string, defaultPlan?: DefaultPlanResponse): CoverageSettings {
        // If no planId provided, use defaults
        if (!planIdQueryValue) {
            return DefaultPlanUtils.defaultPlanToCoverageSettings(defaultPlan, "ptz-us");
        }

        try {
            return DefaultPlanUtils.transformPlanToCoverageSettings(planIdQueryValue, defaultPlan);
        } catch (error) {
            console.error("Failed to parse planId:", planIdQueryValue, error);
            // Fallback to defaults on parse error
            return DefaultPlanUtils.defaultPlanToCoverageSettings(defaultPlan, "ptz-us");
        }
    }

    /**
     * Check if all policies in a quote match the target coverage settings from planId
     */
    static doesQuoteMatchPlanIdCoverage(quote: Quote, targetCoverageSettings: CoverageSettings): boolean {
        // If quote has no policies, consider it a mismatch
        if (!quote.policies || quote.policies.length === 0) {
            return false;
        }

        // Check each policy's coverage settings against target
        return quote.policies.every(policy => {
            const policyCoverageSettings = policy.coverageSettings;

            // If policy doesn't have coverage settings, it's a mismatch
            if (!policyCoverageSettings) {
                return false;
            }

            return DefaultPlanUtils.doCoverageSettingsMatch(policyCoverageSettings, targetCoverageSettings);
        });
    }

    /**
     * Compare two CoverageSettings objects using react-fast-compare
     * Normalizes objects to focus on key fields before comparison
     */
    static doCoverageSettingsMatch(settings1: CoverageSettings, settings2: CoverageSettings): boolean {
        // Normalize both settings to only include the fields we care about
        const normalized1 = DefaultPlanUtils.normalizeCoverageSettingsForComparison(settings1);
        const normalized2 = DefaultPlanUtils.normalizeCoverageSettingsForComparison(settings2);

        return isEqual(normalized1, normalized2);
    }

    /**
     * Normalize CoverageSettings to only include fields relevant for planId comparison
     * Ensures consistent structure and ordering for accurate comparison
     */
    static normalizeCoverageSettingsForComparison(settings: CoverageSettings) {
        if (!settings.coverages || !settings.amounts) {
            return null;
        }

        return {
            // Sort coverage types for consistent comparison; wellness riders are not expressible
            // in a planId, so they are excluded from the match (ENG-17824)
            coverageTypes: settings.coverages
                .map(coverage => coverage.type)
                .filter(type => !type?.startsWith("wellness"))
                .sort(),

            // The key amounts that change with planId
            amounts: {
                reimbursementRate: settings.amounts.reimbursementRate,
                annualDeductible: settings.amounts.annualDeductible,
                annualLimit: settings.amounts.annualLimit
            },

            // Plan ID from first non-wellness coverage (wellness riders carry rider ids, not plan ids — ENG-17824)
            planId: settings.coverages.find(coverage => !coverage.type?.startsWith("wellness"))?.id
        };
    }
}
